import { useState, useEffect, useCallback } from 'react';

const FAVORITES_STORAGE_KEY = 'quantix-mobile-favorites';

/**
 * Hook to manage favorite instruments using localStorage
 */
export const useFavorites = () => {
    const [favorites, setFavorites] = useState<Set<string>>(new Set());

    // Load favorites from localStorage on mount
    useEffect(() => {
        try {
            const stored = localStorage.getItem(FAVORITES_STORAGE_KEY);
            if (stored) {
                const favoriteIds = JSON.parse(stored) as string[];
                setFavorites(new Set(favoriteIds));
            }
        } catch (error) {
            console.error('Error loading favorites:', error);
        }
    }, []);

    // Save favorites to localStorage whenever they change
    useEffect(() => {
        try {
            localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(Array.from(favorites)));
        } catch (error) {
            console.error('Error saving favorites:', error);
        }
    }, [favorites]);

    const toggleFavorite = useCallback((instrumentId: string) => {
        setFavorites(prev => {
            const newFavorites = new Set(prev);
            if (newFavorites.has(instrumentId)) {
                newFavorites.delete(instrumentId);
            } else {
                newFavorites.add(instrumentId);
            }
            return newFavorites;
        });
    }, []);

    const isFavorite = useCallback(
        (instrumentId: string) => {
            return favorites.has(instrumentId);
        },
        [favorites]
    );

    const addFavorite = useCallback((instrumentId: string) => {
        setFavorites(prev => new Set(prev).add(instrumentId));
    }, []);

    const removeFavorite = useCallback((instrumentId: string) => {
        setFavorites(prev => {
            const newFavorites = new Set(prev);
            newFavorites.delete(instrumentId);
            return newFavorites;
        });
    }, []);

    return {
        favorites: Array.from(favorites),
        isFavorite,
        toggleFavorite,
        addFavorite,
        removeFavorite,
    };
};
